import {
  useCallback,
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  evaluateRevenueIntelligenceChatAccess,
  RevenueIntelligenceChatAccessContext,
  RevenueIntelligenceChatAccessPolicy,
} from "../lib/revenue-intelligence-chat-access";
import { selectRevenueIntelligenceChatCatalogueEntry } from "../lib/revenue-intelligence-chat-catalogue";

export type RevenueIntelligenceChatInsight = {
  insightId: string;
  label: string;
  title: string;
  body: string;
  evidence: string;
  recommendedAction?: string;
  sourceHelper: string;
  sourceLabel?: string;
  sourceFields?: string[];
  requiredAccess?: string;
  caveat?: string;
  topics?: string[];
  signal?: {
    availableActions: Array<"explain" | "recommend" | "view_evidence" | "open_deal" | "mark_seen" | "snooze">;
    identity: {
      signalId: string;
      sourceFingerprint: string;
    };
    lifecycleState: "new" | "active" | "acknowledged" | "snoozed" | "resolved" | "dismissed";
    linkedRecordIds: string[];
    priorityDimensions: {
      businessImpact?: string;
      confidence?: number;
      novelty?: "new" | "known";
      severity?: string;
      signalStrength?: number;
      urgency?: string;
    };
  };
};

export type RevenueIntelligenceChatDealFact = {
  dealId: string | number;
  name: string;
  value: number;
  valueLabel: string;
  statusLabel?: string;
  stageLabel?: string;
  expectedCloseDateLabel?: string;
  closedDateLabel?: string;
  riskLabel?: string;
};

export type RevenueIntelligenceChatStageSummary = {
  stageLabel: string;
  count: number;
  totalValue: number;
  totalValueLabel: string;
};

export type RevenueIntelligenceChatScope = {
  pageLabel: string;
  periodLabel?: string;
  filtersLabel?: string;
};

export type RevenueIntelligenceChatFacts = {
  openDealCount: number;
  openPipelineValueLabel: string;
  largestOpenDeal?: RevenueIntelligenceChatDealFact;
  smallestOpenDeal?: RevenueIntelligenceChatDealFact;
  topOpenDeals: RevenueIntelligenceChatDealFact[];
  openPipelineByStage: RevenueIntelligenceChatStageSummary[];
  expectedToCloseThisMonth: RevenueIntelligenceChatDealFact[];
  expectedToCloseThisQuarter: RevenueIntelligenceChatDealFact[];
  expectedToCloseNext60Days: RevenueIntelligenceChatDealFact[];
  wonDealsThisMonth: RevenueIntelligenceChatDealFact[];
  wonDealsThisQuarter: RevenueIntelligenceChatDealFact[];
  lostDealsThisMonth: RevenueIntelligenceChatDealFact[];
  lostDealsThisQuarter: RevenueIntelligenceChatDealFact[];
  wonRevenueThisMonthLabel: string;
  wonRevenueThisQuarterLabel: string;
  lostDealValueThisMonthLabel: string;
  lostDealValueThisQuarterLabel: string;
  averageOpenDealSizeLabel: string;
  source: string;
  requiredAccess?: string;
  scope?: RevenueIntelligenceChatScope;
};

export type RevenueIntelligenceChatMetric = {
  metricId: string;
  label: string;
  aliases: string[];
  valueLabel: string;
  definition: string;
  includedRecords: string;
  dateField: string;
  valueBasis: string;
  currencyHandling: string;
  probabilityOrRiskBasis: string;
  sourceHelper: string;
  sourceLabel?: string;
  sourceFields: string[];
  caveats?: string[];
};

export type RevenueIntelligenceChatRecord = {
  id: string;
  label: string;
  details: string[];
  values: Record<string, string | number | null>;
  displayValues?: Record<string, string>;
};

export type RevenueIntelligenceChatRecordField = {
  field: string;
  label: string;
  aliases: string[];
  semanticRoles?: Array<
    | "identifier"
    | "evidence"
    | "explanation"
    | "recommendation"
    | "measure"
    | "dimension"
    | "time"
  >;
  allowedFilterValues?: Array<{
    value: string;
    label: string;
    aliases: string[];
  }>;
  type: "category" | "currency" | "date" | "number" | "percentage";
  metricId?: string;
  sortable: boolean;
  filterable?: boolean;
};

export type RevenueIntelligenceChatRecordSet = {
  recordSetId: string;
  label: string;
  aliases: string[];
  responseTitle: string;
  fields: RevenueIntelligenceChatRecordField[];
  records: RevenueIntelligenceChatRecord[];
  defaultSort: {
    field: string;
    direction: "asc" | "desc";
  };
  rankingDescription: string;
  sourceHelper: string;
  sourceLabel?: string;
  sourceFields: string[];
  emptyMessage: string;
  caveats?: string[];
};

export type RevenueIntelligenceChatChartType = "bar" | "line" | "scatter";

export type RevenueIntelligenceChatDatasetField = {
  field: string;
  label: string;
  type: "category" | "currency" | "date" | "number" | "percentage";
  metricId?: string;
};

export type RevenueIntelligenceChatDatasetRow = Record<
  string,
  string | number | null
>;

export type RevenueIntelligenceChatChartSpec = {
  type: RevenueIntelligenceChatChartType;
  title: string;
  xField: string;
  yFields: string[];
  groupBy: string | null;
  showConclusion: boolean;
};

export type RevenueIntelligenceChatDataset = {
  datasetId: string;
  label: string;
  description: string;
  dimensions: RevenueIntelligenceChatDatasetField[];
  measures: RevenueIntelligenceChatDatasetField[];
  rows: RevenueIntelligenceChatDatasetRow[];
  allowedChartTypes: RevenueIntelligenceChatChartType[];
  defaultChart: RevenueIntelligenceChatChartSpec;
  sourceHelper: string;
  sourceFields: string[];
  caveats?: string[];
};

export type RevenueIntelligenceChatRegistrationValue = {
  accessPolicy: RevenueIntelligenceChatAccessPolicy;
  contractId: string;
  facts?: RevenueIntelligenceChatFacts | null;
  insights: RevenueIntelligenceChatInsight[];
  metrics: RevenueIntelligenceChatMetric[];
  recordSets: RevenueIntelligenceChatRecordSet[];
  datasets: RevenueIntelligenceChatDataset[];
  scope: RevenueIntelligenceChatScope;
  scopeKey: string;
  suggestedQuestions: string[];
  topics: string[];
};

export type RevenueIntelligenceChatCatalogueEntry = {
  catalogueId: string;
  contractIds: string[];
  label: string;
  topics: string[];
  suggestedQuestions: string[];
  accessPolicy: RevenueIntelligenceChatAccessPolicy;
  unavailableReason?: string;
  resolve?: () => Promise<RevenueIntelligenceChatRegistrationValue[]>;
};

export type RevenueIntelligenceChatCatalogueResolution =
  | { status: "not_requested" }
  | {
      status: "resolved";
      entry: RevenueIntelligenceChatCatalogueEntry;
      contracts: RevenueIntelligenceChatRegistrationValue[];
    }
  | {
      status: "unavailable" | "failed";
      entry: RevenueIntelligenceChatCatalogueEntry;
      reason: string;
    };

export type RevenueIntelligenceChatScopeAction = {
  actionId: string;
  aliases?: string[];
  field?: "owner" | "period" | "pipeline" | "team";
  label: string;
  values?: string[];
  apply: () => void;
  resolve?: (
    actions: RevenueIntelligenceChatScopeAction[],
  ) => Promise<RevenueIntelligenceChatRegistrationValue[]> | RevenueIntelligenceChatRegistrationValue[];
};

type RevenueIntelligenceChatContextValue = {
  activeContractIds: string[];
  insights: RevenueIntelligenceChatInsight[];
  facts: RevenueIntelligenceChatFacts | null;
  accessKey: string;
  actorAccessKey: string;
  accessNotice: string | null;
  metrics: RevenueIntelligenceChatMetric[];
  recordSets: RevenueIntelligenceChatRecordSet[];
  contracts: RevenueIntelligenceChatRegistrationValue[];
  catalogueEntries: RevenueIntelligenceChatCatalogueEntry[];
  datasets: RevenueIntelligenceChatDataset[];
  scopeActions: RevenueIntelligenceChatScopeAction[];
  suggestedQuestions: string[];
  registerContract: (registration: RevenueIntelligenceChatRegistrationValue) => void;
  registerCatalogueEntry: (entry: RevenueIntelligenceChatCatalogueEntry) => void;
  registerScopeActions: (
    scopeKey: string,
    actions: RevenueIntelligenceChatScopeAction[],
  ) => void;
  unregisterContract: (contractId: string) => void;
  unregisterCatalogueEntry: (catalogueId: string) => void;
  unregisterScopeActions: (scopeKey: string) => void;
  resolveContractsForQuestion: (
    question: string,
    activeContractIds: string[],
  ) => Promise<RevenueIntelligenceChatCatalogueResolution>;
};

const RevenueIntelligenceChatContext = createContext<RevenueIntelligenceChatContextValue | null>(null);

export function RevenueIntelligenceChatProvider({
  accessContext,
  children,
}: PropsWithChildren<{
  accessContext: RevenueIntelligenceChatAccessContext | null;
}>) {
  const [registrations, setRegistrations] = useState<
    Record<string, RevenueIntelligenceChatRegistrationValue>
  >({});
  const [catalogueRegistrations, setCatalogueRegistrations] = useState<
    Record<string, RevenueIntelligenceChatCatalogueEntry>
  >({});
  const [scopeActionRegistrations, setScopeActionRegistrations] = useState<
    Record<string, RevenueIntelligenceChatScopeAction[]>
  >({});

  const registerContract = useCallback((registration: RevenueIntelligenceChatRegistrationValue) => {
    setRegistrations(current => ({
      ...current,
      [registration.contractId]: registration,
    }));
  }, []);

  const unregisterContract = useCallback((contractId: string) => {
    setRegistrations((current) => {
      if (!(contractId in current)) return current;
      const next = { ...current };
      delete next[contractId];
      return next;
    });
  }, []);

  const registerCatalogueEntry = useCallback((entry: RevenueIntelligenceChatCatalogueEntry) => {
    setCatalogueRegistrations(current => ({
      ...current,
      [entry.catalogueId]: entry,
    }));
  }, []);

  const unregisterCatalogueEntry = useCallback((catalogueId: string) => {
    setCatalogueRegistrations((current) => {
      if (!(catalogueId in current)) return current;
      const next = { ...current };
      delete next[catalogueId];
      return next;
    });
  }, []);

  const registerScopeActions = useCallback((
    scopeKey: string,
    actions: RevenueIntelligenceChatScopeAction[],
  ) => {
    setScopeActionRegistrations(current => ({
      ...current,
      [scopeKey]: actions,
    }));
  }, []);

  const unregisterScopeActions = useCallback((scopeKey: string) => {
    setScopeActionRegistrations((current) => {
      if (!(scopeKey in current)) return current;
      const next = { ...current };
      delete next[scopeKey];
      return next;
    });
  }, []);

  const authorizedCatalogueEntries = useMemo(() => (
    Object.values(catalogueRegistrations).filter(entry => (
      evaluateRevenueIntelligenceChatAccess(accessContext, entry.accessPolicy).allowed
    ))
  ), [accessContext, catalogueRegistrations]);

  const resolveContractsForQuestion = useCallback(async (
    question: string,
    activeContractIds: string[],
  ): Promise<RevenueIntelligenceChatCatalogueResolution> => {
    const selected = selectRevenueIntelligenceChatCatalogueEntry(
      question,
      authorizedCatalogueEntries,
      activeContractIds,
    );
    if (!selected || selected.active) return { status: "not_requested" };
    const { entry } = selected;
    if (!entry.resolve) {
      return {
        status: "unavailable",
        entry,
        reason: entry.unavailableReason ?? `${entry.label} is not available outside its view yet.`,
      };
    }
    try {
      const contracts = await entry.resolve();
      const authorizedContracts = contracts.filter(contract => (
        evaluateRevenueIntelligenceChatAccess(accessContext, contract.accessPolicy).allowed
      ));
      if (!authorizedContracts.length) {
        return {
          status: "failed",
          entry,
          reason: `${entry.label} could not be loaded for this scope.`,
        };
      }
      return { status: "resolved", entry, contracts: authorizedContracts };
    } catch {
      return {
        status: "failed",
        entry,
        reason: `${entry.label} could not be loaded right now.`,
      };
    }
  }, [accessContext, authorizedCatalogueEntries]);

  const value = useMemo(() => {
    const activeRegistrations = Object.values(registrations);
    const authorizedRegistrations = activeRegistrations.filter(registration => (
      evaluateRevenueIntelligenceChatAccess(
        accessContext,
        registration.accessPolicy,
      ).allowed
    ));
    const hasRestrictedRegistrations = authorizedRegistrations.length < activeRegistrations.length
      || authorizedCatalogueEntries.length < Object.values(catalogueRegistrations).length;
    const activeContractIds = authorizedRegistrations.map(registration => registration.contractId);
    return {
      activeContractIds,
      actorAccessKey: JSON.stringify(accessContext),
      accessKey: JSON.stringify({
        actor: accessContext,
        contracts: authorizedRegistrations.map(registration => ({
          accessPolicy: registration.accessPolicy,
          contractId: registration.contractId,
          scopeKey: registration.scopeKey,
        })),
      }),
      accessNotice: hasRestrictedRegistrations
        ? "Your role and data scope limit which Revenue Intelligence answers are available. Company-wide, team or another seller's data is shown only when your access explicitly allows it."
        : null,
      facts: authorizedRegistrations.find(registration => registration.facts)?.facts ?? null,
      catalogueEntries: authorizedCatalogueEntries,
      contracts: authorizedRegistrations,
      datasets: authorizedRegistrations.flatMap(registration => registration.datasets),
      insights: authorizedRegistrations.flatMap(registration => registration.insights),
      metrics: authorizedRegistrations.flatMap(registration => registration.metrics),
      recordSets: authorizedRegistrations.flatMap(registration => registration.recordSets),
      scopeActions: Object.values(scopeActionRegistrations).flat(),
      suggestedQuestions: [
        ...new Set([
          ...authorizedRegistrations.flatMap(registration => registration.suggestedQuestions),
          ...authorizedCatalogueEntries.flatMap(entry => entry.suggestedQuestions),
        ]),
      ],
      registerCatalogueEntry,
      registerContract,
      registerScopeActions,
      resolveContractsForQuestion,
      unregisterCatalogueEntry,
      unregisterContract,
      unregisterScopeActions,
    };
  }, [
    accessContext,
    authorizedCatalogueEntries,
    catalogueRegistrations,
    registerCatalogueEntry,
    registerContract,
    registerScopeActions,
    registrations,
    scopeActionRegistrations,
    resolveContractsForQuestion,
    unregisterCatalogueEntry,
    unregisterContract,
    unregisterScopeActions,
  ]);

  return (
    <RevenueIntelligenceChatContext.Provider value={value}>
      {children}
    </RevenueIntelligenceChatContext.Provider>
  );
}

// Provider hooks intentionally live next to the provider, matching the app pattern.
// eslint-disable-next-line react-refresh/only-export-components
export function useRevenueIntelligenceChat() {
  const context = useContext(RevenueIntelligenceChatContext);
  if (!context) {
    throw new Error("useRevenueIntelligenceChat must be used within RevenueIntelligenceChatProvider");
  }

  return context;
}
